import { FormControl } from '@angular/forms'
import {
  NOTE_MAX_LENGTH,
  NoteContentValidator,
  checkIsNoteValid,
} from '@helios/validators'
import { Break, BreakResponseObject } from '../model/break.model'
import {
  BulkBreakGroup,
  allBreaksNoted,
  isBreakCoded,
  isBreakNoted,
} from '../model/bulk-break-group.model'
import { unescapeNoteNewlines } from '../utils/note-formatter'
import {
  actionCodeOptions,
  computeBreakDialogValidation,
  descriptionCodeOptions,
  notesRequiredActionCodes,
  prefillSelection,
} from './break-code-validation'

export type KeepMode = 'keep' | 'override'

export interface GroupSelection {
  actionCode: string | null
  descriptionCode: string | null
  note: string
}

export interface GroupValidation {
  complete: boolean
  blocked: boolean
  noteRequired: boolean
  reason?: string
}

export const MIXED_CODE_SETS_REASON =
  'This break type spans multiple code sets — split your selection by client.'

/**
 * Breaks a save would write the given FIELD to: all of them in override; in keep, only
 * those whose own value for THAT field is empty, and only if a code is configured to
 * fill it with. Per field, not per break so a break coded on action only is still
 * a write target for its description.
 */
export function writeTargets(
  group: BulkBreakGroup,
  keepMode: KeepMode,
  field: 'action' | 'description',
): Break[] {
  if (keepMode === 'override') return group.breaks
  const av = group.availability
  const configured =
    av.status === 'ready' &&
    (field === 'action' ? av.actionCodes : av.descriptionCodes).length > 0
  if (!configured) return []
  return group.breaks.filter(b =>
    field === 'action' ? !b.actionCode : !b.descriptionCode,
  )
}

/**
 * The selection a freshly-rendered group starts with: the breaks' existing codes when
 * a single distinct one is still configured, plus their shared note unless
 * WITH_NOTE is false. Under Append the note box means "text to add": Helios's
 * `getNote` (BreakNoteHandler.kt) joins old + new without deduping, so a prefilled
 * note would be appended to itself. The group component prefills its form from this
 * and the dialog seeds `selections` with the same values, so the child's initial
 * emit changes nothing mid-change-detection.
 */
export function prefillGroupSelection(
  group: BulkBreakGroup,
  withNote = true,
): GroupSelection {
  const notes = group.breaks.map(b => unescapeNoteNewlines(b.note))
  const shared = notes.length > 0 && notes.every(n => n === notes[0])
  return {
    actionCode:
      prefillSelection(
        group.breaks.map(b => b.actionCode),
        actionCodeOptions(group.availability),
      ) ?? null,
    descriptionCode:
      prefillSelection(
        group.breaks.map(b => b.descriptionCode),
        descriptionCodeOptions(group.availability),
      ) ?? null,
    note: withNote && shared ? notes[0] : '',
  }
}

/** A group is actionable when it has any selected breaks to hold/accept. */
export function isGroupActionable(group: BulkBreakGroup): boolean {
  return group.breaks.length > 0
}

/** Whether a save would write the chosen code for FIELD to any break in the group. */
export function hasWriteTargetFor(
  group: BulkBreakGroup,
  keepMode: KeepMode,
  field: 'action' | 'description',
): boolean {
  return writeTargets(group, keepMode, field).length > 0
}

/** Whether a save would write EITHER code to any break in this group. */
export function hasWriteTarget(
  group: BulkBreakGroup,
  keepMode: KeepMode,
): boolean {
  return (
    hasWriteTargetFor(group, keepMode, 'action') ||
    hasWriteTargetFor(group, keepMode, 'description')
  )
}

/**
 * A kept (preserved) notes-required action code on a break that has no note of its
 * own. Per break, not group-wide: a break whose notes-required code already carries
 * its note needs nothing, and demanding one anyway would make the operator type a
 * note that REPLACE then writes over the good one.
 */
function keepPreservesNotesRequiredCode(group: BulkBreakGroup): boolean {
  const required = notesRequiredActionCodes(group.availability)
  return group.breaks.some(
    b => b.actionCode != null && required.has(b.actionCode) && !isBreakNoted(b),
  )
}

/**
 * A *provided* note (any non-empty) must itself be a valid note — ≥3 words and
 * allowed content (≤1024 chars, no `<`/`>`) — even when it's optional, because it
 * overwrites the selected breaks. Mirrors the single Hold/Accept and Edit-Note
 * dialogs. Returns a reason when an entered note is invalid, else null. (An empty
 * note is a no-op handled by the note-required logic, so it's not flagged here.)
 */
function providedNoteProblem(note: string): string | null {
  if (note.length === 0) return null
  if (!checkIsNoteValid(note)) return 'Note must be at least 3 words.'
  const contentError = NoteContentValidator()(new FormControl(note))
  if (contentError) {
    return 'NoteTooLong' in contentError
      ? `Note must be at most ${NOTE_MAX_LENGTH} characters.`
      : `Note contains invalid characters (e.g. '<' or '>').`
  }
  return null
}

/**
 * Per-group validation. mixedCodeSets is blocked with a split-by-client message.
 * A keep-mode group with codes ALREADY ON ITS BREAKS and no write target left on
 * either field has nothing to apply, so no chosen code is required (status/note
 * still apply, codes preserved). Everything else — including a break type that
 * configures no codes at all, and codes that failed to load — delegates to the
 * single-unit computeBreakDialogValidation, which owns those rules.
 */
export function validateGroup(
  group: BulkBreakGroup,
  selection: GroupSelection,
  keepMode: KeepMode,
): GroupValidation {
  if (group.availability.status === 'mixedCodeSets') {
    return {
      complete: false,
      blocked: true,
      noteRequired: false,
      reason: MIXED_CODE_SETS_REASON,
    }
  }

  const allNoted = allBreaksNoted(group.breaks)

  // An empty write-target set has three causes and only one of them means "nothing
  // left to do": every break already fills that field. The other two — the type
  // configures no code for it, and the codes never loaded — must NOT short-circuit,
  // or a break saves with neither a code nor a note. Requiring `ready` plus EVERY
  // break carrying a code separates them: `some` would let a bare break ride along
  // beside a coded one on a type that configures nothing, taking neither.
  const preservingExistingCodes =
    group.availability.status === 'ready' &&
    group.breaks.every(isBreakCoded) &&
    !hasWriteTarget(group, keepMode)

  let base: GroupValidation
  if (preservingExistingCodes) {
    // Codes are preserved, so no chosen code is needed; only a preserved
    // notes-required code still demands anything.
    const noteRequired = keepPreservesNotesRequiredCode(group)
    const complete = !noteRequired || checkIsNoteValid(selection.note)
    base = {
      complete,
      blocked: false,
      noteRequired,
      reason: complete
        ? undefined
        : 'This action code requires a note (3 words minimum).',
    }
  } else {
    // Per field, so the field whose select is hidden can't block Save with a reason
    // the user has no control to satisfy. preservedNotesRequired covers the code the
    // user never chose: Keep preserves it, its select may be hidden, and it still
    // demands a note.
    const result = computeBreakDialogValidation({
      availability: group.availability,
      selectedActionCode: selection.actionCode,
      selectedDescriptionCode: selection.descriptionCode,
      note: selection.note,
      allBreaksAlreadyNoted: allNoted,
      actionWritable: hasWriteTargetFor(group, keepMode, 'action'),
      descriptionWritable: hasWriteTargetFor(group, keepMode, 'description'),
      preservedNotesRequired:
        keepMode === 'keep' && keepPreservesNotesRequiredCode(group),
    })
    base = {
      complete: !result.submitDisabled,
      blocked: false,
      noteRequired: result.noteRequired,
      reason: result.reasons[0],
    }
  }

  // A provided note must itself be valid, even when optional.
  // Only layer this on when the group is
  // otherwise complete — an empty/required-note blocker keeps its own reason.
  if (base.complete) {
    const noteProblem = providedNoteProblem(selection.note)
    if (noteProblem) {
      return { ...base, complete: false, reason: noteProblem }
    }
  }
  return base
}

const EMPTY_SELECTION: GroupSelection = {
  actionCode: null,
  descriptionCode: null,
  note: '',
}

/**
 * Dialog-level validation. Submit is disabled when there are no groups at all
 * or any group is incomplete. Every selected group is actionable (held/accepted),
 * and a keep group with nothing left to write is complete, so the previous
 * empty-reasons dead-end no longer occurs. Reasons are the per-incomplete-group blockers,
 * prefixed with the break type.
 */
export function bulkDialogValidation(
  groups: BulkBreakGroup[],
  selections: Map<string, GroupSelection>,
  keepMode: KeepMode,
): { submitDisabled: boolean; reasons: string[] } {
  const actionableGroups = groups.filter(isGroupActionable)
  if (actionableGroups.length === 0) {
    return { submitDisabled: true, reasons: ['No breaks selected.'] }
  }

  const reasons: string[] = []
  let allComplete = true
  for (const g of actionableGroups) {
    const v = validateGroup(
      g,
      selections.get(g.breakType) ?? EMPTY_SELECTION,
      keepMode,
    )
    if (!v.complete) {
      allComplete = false
      if (v.reason) reasons.push(`${g.displayName ?? g.breakType}: ${v.reason}`)
    }
  }
  return { submitDisabled: !allComplete, reasons }
}

export { EMPTY_SELECTION }

export interface SaveDescriptor {
  breakType: string
  breakIds: string[]
  restrictedBreakIds: string[]
  actionCode?: string
  descriptionCode?: string
  note: string
  preserveExistingBreakCodes: boolean
}

/**
 * Whether a break needs approval for the given mode. Hold is restricted only
 * when both actions are restricted ('HOLD,ACCEPT'); Accept when any restriction
 * applies. Mirrors the single Hold/Accept dialogs.
 */
export function isRestrictedForMode(
  b: Break,
  mode: 'hold' | 'accept',
): boolean {
  return mode === 'hold'
    ? b.restrictedAction === 'HOLD,ACCEPT'
    : b.restrictedAction != null
}

/**
 * One SaveDescriptor per group with any breaks. breakIds is ALL the group's
 * breaks — status/expiry/note apply to every break; codes apply per the
 * preserveExistingBreakCodes flag (true in keep mode, false in override).
 * restrictedBreakIds is the subset of ALL breaks blocked for the given mode —
 * HOLD,ACCEPT-restricted on hold, any restriction on accept.
 */
export function groupsToSaveDescriptors(
  groups: BulkBreakGroup[],
  selections: Map<string, GroupSelection>,
  keepMode: KeepMode,
  mode: 'hold' | 'accept',
): SaveDescriptor[] {
  const descriptors: SaveDescriptor[] = []
  for (const group of groups) {
    if (!isGroupActionable(group)) {
      continue
    }
    const restricted = group.breaks.filter(b => isRestrictedForMode(b, mode))
    const selection = selections.get(group.breakType) ?? EMPTY_SELECTION
    descriptors.push({
      breakType: group.breakType,
      breakIds: group.breaks.map(b => b.identifier),
      restrictedBreakIds: restricted.map(b => b.identifier),
      actionCode: selection.actionCode ?? undefined,
      descriptionCode: selection.descriptionCode ?? undefined,
      note: selection.note,
      preserveExistingBreakCodes: keepMode === 'keep',
    })
  }
  return descriptors
}

/** Smallest defined hold limit across the breaks, or undefined if none. */
export function minHoldLimitInDays(breaks: Break[]): number | undefined {
  const limits = breaks
    .map(b => b.actionLimits?.holdLimitInDays)
    .filter((d): d is number => d != null)
  return limits.length > 0 ? Math.min(...limits) : undefined
}

/** Merge per-group save responses into one combined response. */
export function aggregateResponses(
  responses: BreakResponseObject[],
): BreakResponseObject {
  return {
    commands: responses.flatMap(r => r.commands),
    breakValidationResults: responses.flatMap(r => r.breakValidationResults),
  }
}
